import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { oneKlicks } from '../SDK/oneclick';
import { toast } from 'react-toastify';
import UserChat from './UserChat';
import ImageShow from '../Uitiles/ImageShow';
import { Badge, Form, InputGroup } from 'react-bootstrap';
import { Button, Select } from 'antd';
import { PlusOutlined, MessageOutlined, CalendarOutlined, CloseOutlined, SearchOutlined } from '@ant-design/icons';

const UserAppointments = () => {
    const { userData } = useAuth();
    const [appointments, setAppointments] = useState([]);
    const [filteredAppointments, setFilteredAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [userProfile, setUserProfile] = useState(null);
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const [showChat, setShowChat] = useState(false);
    const [doctorImages, setDoctorImages] = useState({}); // Cache for doctor images

    // Enhanced filtering and search
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [dateFilter, setDateFilter] = useState('all');
    const [sortBy, setSortBy] = useState('date');

    useEffect(() => {
        fetchUserProfile();
    }, []);

    useEffect(() => {
        if (userProfile || userData?.phone) {
            fetchAppointments();
        }
    }, [userProfile, userData?.phone]);

    // Filter and sort appointments
    useEffect(() => {
        let filtered = [...appointments];

        // Search filter
        if (searchTerm) {
            filtered = filtered.filter(apt =>
                apt.doctor_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                apt.note?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                apt.display_amount?.toString().includes(searchTerm)
            );
        }

        // Status filter
        if (statusFilter !== 'all') {
            filtered = filtered.filter(apt => apt.status === statusFilter);
        }

        // Date filter
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        switch (dateFilter) {
            case 'today':
                filtered = filtered.filter(apt => {
                    const aptDate = new Date(apt.appointment_date);
                    aptDate.setHours(0, 0, 0, 0);
                    return aptDate.getTime() === today.getTime();
                });
                break;
            case 'tomorrow':
                const tomorrow = new Date(today);
                tomorrow.setDate(tomorrow.getDate() + 1);
                filtered = filtered.filter(apt => {
                    const aptDate = new Date(apt.appointment_date);
                    aptDate.setHours(0, 0, 0, 0);
                    return aptDate.getTime() === tomorrow.getTime();
                });
                break;
            case 'this_week':
                const weekStart = new Date(today);
                weekStart.setDate(weekStart.getDate() - weekStart.getDay());
                const weekEnd = new Date(weekStart);
                weekEnd.setDate(weekEnd.getDate() + 6);
                filtered = filtered.filter(apt => {
                    const aptDate = new Date(apt.appointment_date);
                    return aptDate >= weekStart && aptDate <= weekEnd;
                });
                break;
            case 'upcoming':
                filtered = filtered.filter(apt => new Date(apt.appointment_date) > today);
                break;
            case 'past':
                filtered = filtered.filter(apt => new Date(apt.appointment_date) < today);
                break;
        }

        // Sort
        filtered.sort((a, b) => {
            switch (sortBy) {
                case 'date':
                    return new Date(b.appointment_date) - new Date(a.appointment_date);
                case 'doctor':
                    return (a.doctor_name || '').localeCompare(b.doctor_name || '');
                case 'status':
                    return (a.status || '').localeCompare(b.status || '');
                case 'amount':
                    return (b.amount || 0) - (a.amount || 0);
                default:
                    return 0;
            }
        });

        setFilteredAppointments(filtered);
    }, [appointments, searchTerm, statusFilter, dateFilter, sortBy]);

    const fetchUserProfile = async () => {
        try {
            const tableSchema = '';
            const tableName = 'users';
            const conditions = { id: userData?.id };
            const response = await oneKlicks.read(tableSchema, tableName, conditions, '', '', '', '');

            if (response.success && response.data.length > 0) {
                setUserProfile(response.data[0]);
            }
        } catch (error) {
            console.error('Error fetching user profile:', error);
            toast.error('Failed to load user profile');
        }
    };

    const fetchAppointments = async () => {
        try {
            setLoading(true);
            const tableSchema = '';
            const tableName = 'appointments';

            const conditions = userData?.id
                ? { patient_id: userData.id }
                : { patient_phone: userProfile?.phone || userData?.phone };

            const margedata = [
                {
                    target_table: 'users',
                    target_column: 'id',
                    target_value: 'doctor_id',
                    target_label: 'doctor'
                }
            ];

            const response = await oneKlicks.read(
                tableSchema,
                tableName,
                conditions,
                'created_at DESC',
                '', '', margedata
            );

            if (response.success) {
                const appointmentsData = response.data || [];
                setAppointments(appointmentsData);

                // Fetch doctor profile photos for appointments that don't have doctor_photo
                const doctorIds = [...new Set(appointmentsData
                    .filter(apt => apt.doctor_id && !apt.doctor_photo)
                    .map(apt => apt.doctor_id)
                )];

                if (doctorIds.length > 0) {
                    fetchDoctorImages(doctorIds);
                }
            } else {
                setAppointments([]);
            }
        } catch (error) {
            console.error('Error fetching appointments:', error);
            toast.error('Failed to load appointments');
        } finally {
            setLoading(false);
        }
    };

    const fetchDoctorImages = async (doctorIds) => {
        try {
            const imageMap = {};

            // Fetch doctor details for each unique doctor ID
            for (const doctorId of doctorIds) {
                try {
                    const tableSchema = '';
                    const tableName = 'doctors';
                    const conditions = { id: doctorId };

                    const response = await oneKlicks.read(
                        tableSchema,
                        tableName,
                        conditions,
                        '',
                        '',
                        '',
                        ''
                    );

                    if (response.success && response.data && response.data.length > 0) {
                        const doctor = response.data[0];
                        if (doctor.profile_photo_url) {
                            imageMap[doctorId] = doctor.profile_photo_url;
                        }
                    }
                } catch (error) {
                    console.error(`Error fetching doctor ${doctorId} image:`, error);
                }
            }

            setDoctorImages(prev => ({ ...prev, ...imageMap }));
        } catch (error) {
            console.error('Error fetching doctor images:', error);
        }
    };

    const handleReschedule = async (appointmentId, newDate, newTime) => {
        try {
            const tableSchema = '';
            const tableName = 'appointments';
            const data = {
                appointment_date: newDate,
                appointment_time: newTime,
                updated_at: new Date().toISOString().slice(0, 19).replace('T', ' ')
            };
            const conditions = { id: appointmentId };

            const response = await oneKlicks.update(tableSchema, tableName, data, conditions);

            if (response.success) {
                toast.success('Appointment rescheduled successfully');
                fetchAppointments();
            } else {
                toast.error('Failed to reschedule appointment');
            }
        } catch (error) {
            console.error('Error rescheduling appointment:', error);
            toast.error('Failed to reschedule appointment');
        }
    };

    const handleCancelAppointment = async (appointmentId) => {
        if (window.confirm('Are you sure you want to cancel this appointment?')) {
            try {
                const tableSchema = '';
                const tableName = 'appointments';
                const data = {
                    status: 'cancelled',
                    updated_at: new Date().toISOString().slice(0, 19).replace('T', ' ')
                };
                const conditions = { id: appointmentId };

                const response = await oneKlicks.update(tableSchema, tableName, data, conditions);

                if (response.success) {
                    toast.success('Appointment cancelled successfully');
                    fetchAppointments();
                } else {
                    toast.error('Failed to cancel appointment');
                }
            } catch (error) {
                console.error('Error cancelling appointment:', error);
                toast.error('Failed to cancel appointment');
            }
        }
    };

    const handleChatWithDoctor = (doctorId, doctorName, appointmentId) => {
        setSelectedAppointment({ doctorId, doctorName, appointmentId });
        setShowChat(true);
    };

    const closeChat = () => {
        setShowChat(false);
        setSelectedAppointment(null);
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        try {
            let date;
            if (typeof dateString === 'string') {
                if (dateString.includes('T')) {
                    date = new Date(dateString);
                } else if (dateString.includes('-')) {
                    const dateParts = dateString.split(' ')[0].split('-');
                    if (dateParts.length === 3) {
                        date = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);
                    } else {
                        date = new Date(dateString);
                    }
                } else {
                    date = new Date(dateString);
                }
            } else {
                date = new Date(dateString);
            }

            if (isNaN(date.getTime())) {
                return "N/A";
            }

            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (error) {
            console.error("Error formatting date:", error, dateString);
            return "N/A";
        }
    };

    const formatTime = (timeString) => {
        if (!timeString) return "N/A";
        try {
            const [hours, minutes] = timeString.split(':');
            const date = new Date();
            date.setHours(parseInt(hours), parseInt(minutes));
            return date.toLocaleTimeString('en-IN', {
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            return timeString;
        }
    };

    const getStatusBadge = (status) => {
        const statusLower = (status || "").toLowerCase();
        if (statusLower === "confirmed" || statusLower === "upcoming") {
            return <Badge bg="success" style={{ borderRadius: '5px', padding: '5px 10px' }}>{status || "Confirmed"}</Badge>;
        } else if (statusLower === "pending") {
            return <Badge bg="warning" style={{ borderRadius: '5px', padding: '5px 10px', color: '#000' }}>{status || "Pending"}</Badge>;
        } else if (statusLower === "completed" || statusLower === "done") {
            return <Badge bg="info" style={{ borderRadius: '5px', padding: '5px 10px' }}>{status || "Completed"}</Badge>;
        } else if (statusLower === "cancelled" || statusLower === "canceled") {
            return <Badge bg="danger" style={{ borderRadius: '5px', padding: '5px 10px' }}>{status || "Cancelled"}</Badge>;
        }
        return <Badge bg="secondary" style={{ borderRadius: '5px', padding: '5px 10px' }}>{status || "Unknown"}</Badge>;
    };

    const getStatusCount = (status) => {
        return appointments.filter(apt => apt.status === status).length;
    };

    const getDoctorHospitalName = (apt) => {
        if (apt.doctor?.[0]?.display_name) return apt.doctor[0].display_name;
        if (apt.doctor_name) return apt.doctor_name;
        if (apt.hospital_name) return apt.hospital_name;
        if (apt.doctor_display_name) return apt.doctor_display_name;
        if (apt.display_name) return apt.display_name;
        if (apt.first_name || apt.last_name) {
            return `${apt.first_name || ''} ${apt.last_name || ''}`.trim();
        }
        if (apt.doctor_id) return `Doctor ID: ${apt.doctor_id}`;
        if (apt.hospital_id) return `Hospital ID: ${apt.hospital_id}`;
        return "N/A";
    };

    const getDoctorImageUrl = (appointment) => {
        // Priority 0: Joined user profile photo
        if (appointment.doctor?.[0]?.profile_photo_url) {
            return appointment.doctor[0].profile_photo_url;
        }

        // Priority 1: Check if appointment has doctor_photo field (stored during booking)
        if (appointment.doctor_photo) {
            return appointment.doctor_photo;
        }

        // Priority 2: Check if appointment has profile_photo_url field
        if (appointment.profile_photo_url) {
            return appointment.profile_photo_url;
        }

        // Priority 3: Check cached doctor images from doctor_id
        if (appointment.doctor_id && doctorImages[appointment.doctor_id]) {
            return doctorImages[appointment.doctor_id];
        }

        // Priority 4: Check if appointment has doctor_profile_photo_url
        if (appointment.doctor_profile_photo_url) {
            return appointment.doctor_profile_photo_url;
        }

        // Fallback to default placeholder
        return "/uploads/3/doctors/1746963970092_default-placeholder-doctor-halflength-portrait-600nw-1058724875.png";
    };

    if (showChat && selectedAppointment) {
        return (
            <UserChat
                doctorId={selectedAppointment.doctorId}
                doctorName={selectedAppointment.doctorName}
                appointmentId={selectedAppointment.appointmentId}
                onClose={closeChat}
            />
        );
    }

    return (
        <section className="dc-haslayout dc-dbsectionspace">
            <style>
                {`
                    @media (max-width: 767px) {
                        .dc-table-responsive thead {
                            display: none;
                        }
                        .dc-table-responsive tbody tr {
                            display: block;
                            margin-bottom: 15px;
                            border: 1px solid #eee;
                            border-radius: 8px;
                            background: #fff;
                            box-shadow: 0 2px 4px rgba(0,0,0,0.02);
                        }
                        .dc-table-responsive tbody td {
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                            padding: 12px 15px !important;
                            border-bottom: 1px solid #f5f5f5;
                            text-align: right;
                            font-size: 13px;
                            min-height: 45px;
                        }
                        .dc-table-responsive tbody td:last-child {
                            border-bottom: 0;
                        }
                        .dc-table-responsive tbody td::before {
                            content: attr(data-label);
                            font-weight: 700;
                            color: #666;
                            text-transform: uppercase;
                            font-size: 10px;
                            letter-spacing: 0.5px;
                            text-align: left;
                            flex: 1;
                        }
                        .dc-table-responsive tbody td > div, 
                        .dc-table-responsive tbody td > span {
                            flex: 2;
                            display: flex;
                            justify-content: flex-end;
                        }
                        /* Specific alignment for content blocks */
                        .dc-table-responsive .d-flex.align-items-center.gap-2 {
                            justify-content: flex-end;
                        }
                        .dc-table-responsive .justify-content-end {
                            width: 100%;
                        }
                    }
                `}
            </style>
            <div className="subbody" style={{ width: '100%', padding: '2% 4%' }}>
                <div className="container-fluid py-3">
                    {/* Header Card matching Admin/Doctors */}
                    <div className="card mb-5" style={{ borderRadius: '5px' }}>
                        <div className="card-body" style={{ padding: '24px 30px', borderRadius: '5px' }}>
                            <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-3">
                                <div className="d-flex align-items-center gap-2">
                                    <h5 className="mb-0 font-weight-bold">My Appointments</h5>
                                    <Badge bg="info" style={{ borderRadius: '5px' }}>Management</Badge>
                                </div>
                                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <Button
                                            className="btn-outline-primary"
                                            onClick={fetchAppointments}
                                            disabled={loading}
                                            style={{ height: '40px', borderRadius: "5px" }}
                                        >
                                            <CalendarOutlined /> Refresh
                                        </Button>
                                        <Link to="/search-doctors">
                                            <Button
                                                className="btn-primary"
                                                style={{
                                                    height: '40px',
                                                    borderRadius: "5px",
                                                    backgroundColor: 'var(--themecolor)',
                                                    borderColor: 'var(--themecolor)',
                                                    color: '#fff',
                                                    fontWeight: '600',
                                                    transition: 'all 0.3s ease'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.backgroundColor = '#fff';
                                                    e.currentTarget.style.color = 'var(--themecolor)';
                                                    e.currentTarget.style.borderColor = 'var(--themecolor)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.backgroundColor = 'var(--themecolor)';
                                                    e.currentTarget.style.color = '#fff';
                                                    e.currentTarget.style.borderColor = 'var(--themecolor)';
                                                }}
                                            >
                                                <PlusOutlined /> Book New Appointment
                                            </Button>
                                        </Link>
                                    </div>
                                </div>
                            </div>

                            <div className="d-flex flex-wrap align-items-center gap-3" style={{ marginBottom: '15px' }}>
                                <div style={{ flex: '1', minWidth: '250px', maxWidth: '400px' }}>
                                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500', color: '#495057' }}>
                                        Search
                                    </label>
                                    <InputGroup>
                                        <Form.Control
                                            placeholder="Search by doctor, reason, or package..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            style={{ height: '38px', borderRadius: "5px", fontSize: '14px' }}
                                        />
                                    </InputGroup>
                                </div>
                                <div style={{ minWidth: '160px' }}>
                                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500', color: '#495057' }}>
                                        Status
                                    </label>
                                    <Select
                                        value={statusFilter}
                                        onChange={(value) => setStatusFilter(value)}
                                        style={{ width: '100%', height: '38px' }}
                                        options={[
                                            { value: 'all', label: 'All Status' },
                                            { value: 'confirmed', label: 'Confirmed' },
                                            { value: 'pending', label: 'Pending' },
                                            { value: 'completed', label: 'Completed' },
                                            { value: 'cancelled', label: 'Cancelled' },
                                        ]}
                                    />
                                </div>
                                <div style={{ minWidth: '160px' }}>
                                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500', color: '#495057' }}>
                                        History
                                    </label>
                                    <Select
                                        value={dateFilter}
                                        onChange={(value) => setDateFilter(value)}
                                        style={{ width: '100%', height: '38px' }}
                                        options={[
                                            { value: 'all', label: 'All Time' },
                                            { value: 'today', label: 'Today' },
                                            { value: 'tomorrow', label: 'Tomorrow' },
                                            { value: 'upcoming', label: 'Upcoming' },
                                            { value: 'past', label: 'Past' },
                                        ]}
                                    />
                                </div>
                                <div style={{ minWidth: '160px' }}>
                                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500', color: '#495057' }}>
                                        Sort
                                    </label>
                                    <Select
                                        value={sortBy}
                                        onChange={(value) => setSortBy(value)}
                                        style={{ width: '100%', height: '38px' }}
                                        options={[
                                            { value: 'date', label: 'Recent' },
                                            { value: 'doctor', label: 'Name' },
                                            { value: 'status', label: 'Status' },
                                        ]}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Table Card matching Admin/Doctors */}
                    <div className="card" style={{ borderRadius: '5px' }}>
                        <div className="card-body" style={{ padding: '0px', borderRadius: '5px' }}>
                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="mt-2 text-muted">Loading appointments...</p>
                                </div>
                            ) : filteredAppointments.length === 0 ? (
                                <div className="text-center py-5">
                                    <div className="dc-emptydata-holder">
                                        <div className="dc-emptydata">
                                            <div className="dc-emptydetails dc-empty-booking dc-emptyholder-sm">
                                                <span></span>
                                                <em className="text-muted">No appointments found matching your criteria</em>
                                            </div>
                                        </div>
                                    </div>
                                    <Link to="/search-doctors" className="btn btn-primary btn-sm mt-3" style={{ borderRadius: '5px' }}>Book Your First Appointment</Link>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table hover size="sm" className="mb-0 align-middle dc-table-responsive" style={{ width: '100%' }}>
                                        <thead className="bg-light">
                                            <tr style={{ whiteSpace: 'nowrap' }}>
                                                <th style={{ padding: '10px 15px', fontSize: '13px', fontWeight: '600', color: '#666' }}>ID</th>
                                                <th style={{ padding: '10px 15px', fontSize: '13px', fontWeight: '600', color: '#666' }}>Doctor / Clinic</th>
                                                <th style={{ padding: '10px 15px', fontSize: '13px', fontWeight: '600', color: '#666' }}>Schedule</th>
                                                <th style={{ padding: '10px 15px', fontSize: '13px', fontWeight: '600', color: '#666' }}>Note</th>
                                                <th style={{ padding: '10px 15px', fontSize: '13px', fontWeight: '600', color: '#666' }}>Fee</th>
                                                <th style={{ padding: '10px 15px', fontSize: '13px', fontWeight: '600', color: '#666' }}>Status</th>
                                                <th style={{ padding: '10px 15px', fontSize: '13px', fontWeight: '600', color: '#666', textAlign: 'right' }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredAppointments.map((item) => (
                                                <tr key={item.id}>
                                                    <td data-label="ID" style={{ padding: '8px 15px', verticalAlign: 'middle', fontSize: '13px' }}>#{item.id}</td>
                                                    <td data-label="Doctor" style={{ padding: '8px 15px', verticalAlign: 'middle' }}>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <ImageShow
                                                                url={getDoctorImageUrl(item)}
                                                                width={35}
                                                                height={35}
                                                                style={{
                                                                    objectFit: 'cover',
                                                                    borderRadius: '50%',
                                                                    border: '1px solid #eee'
                                                                }}
                                                            />
                                                            <div className="text-end text-md-start">
                                                                <div style={{ fontWeight: '600', color: '#333', fontSize: '13px', lineHeight: '1.2' }}>{getDoctorHospitalName(item)}</div>
                                                                <div style={{ fontSize: '11px', color: '#999' }}>{item.specialization || 'Healthcare'}</div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td data-label="Schedule" style={{ padding: '8px 15px', verticalAlign: 'middle' }}>
                                                        <div className="text-end">
                                                            <div style={{ fontSize: '13px', fontWeight: '500' }}>{formatDate(item.appointment_date)}</div>
                                                            <div style={{ fontSize: '11px', color: '#999' }}>{formatTime(item.appointment_time)}</div>
                                                        </div>
                                                    </td>
                                                    <td data-label="Note" style={{ padding: '8px 15px', verticalAlign: 'middle' }}>
                                                        <div style={{ fontSize: '13px', color: '#333' }}>{item.note || 'Consultation'}</div>
                                                    </td>
                                                    <td data-label="Fee" style={{ padding: '8px 15px', verticalAlign: 'middle' }}>
                                                        <span style={{ fontWeight: '600', fontSize: '13px' }}>₹{item.amount || item.base_amount || '0'}</span>
                                                    </td>
                                                    <td data-label="Status" style={{ padding: '8px 15px', verticalAlign: 'middle' }}>
                                                        {getStatusBadge(item.status)}
                                                    </td>
                                                    <td data-label="Actions" style={{ padding: '8px 15px', verticalAlign: 'middle' }}>
                                                        <div className="d-flex gap-2 justify-content-end align-items-center">
                                                            <Button
                                                                className="fit-content btn-info"
                                                                onClick={() => handleChatWithDoctor(item.doctor_id, item.doctor_name || getDoctorHospitalName(item), item.id)}
                                                                style={{ borderRadius: '4px', width: '32px', height: '32px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                                icon={<MessageOutlined style={{ fontSize: '14px' }} />}
                                                                title="Chat with Doctor"
                                                            />
                                                            {item.status === 'confirmed' && (
                                                                <>
                                                                    <Button
                                                                        className="fit-content"
                                                                        onClick={() => {
                                                                            const newDate = prompt('Enter new date (YYYY-MM-DD):');
                                                                            const newTime = prompt('Enter new time (HH:MM):');
                                                                            if (newDate && newTime) handleReschedule(item.id, newDate, newTime);
                                                                        }}
                                                                        style={{ borderRadius: '4px', width: '32px', height: '32px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                                        icon={<CalendarOutlined style={{ fontSize: '14px' }} />}
                                                                        title="Reschedule"
                                                                    />
                                                                    <Button
                                                                        className="fit-content btn-danger"
                                                                        onClick={() => handleCancelAppointment(item.id)}
                                                                        style={{ borderRadius: '4px', width: '32px', height: '32px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                                        icon={<CloseOutlined style={{ fontSize: '14px' }} />}
                                                                        title="Cancel"
                                                                    />
                                                                </>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default UserAppointments;
